// src/pages/Deposit.tsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { deposit, getTransactions } from "../redux/slices/transactionSlice";
import { GlobalStyles } from "../styles/globalStyles";
import {
  ArrowDownRight,
  CheckCircle,
  AlertCircle,
  Wallet as WalletIcon,
  RefreshCw,
  History,
  TrendingUp,
  Calculator,
} from "lucide-react";
import toast from "react-hot-toast";
import { formatFiat, formatCrypto } from "../utils/currency";
import { transactionService } from "../services/api";

interface CryptoPrice {
  [key: string]: {
    price: number;
    network: string;
    crypto_id: string;
  };
}

// Default currencies with initial values (will be updated when API returns data)
const INITIAL_CURRENCIES = {
  BTC: { price: 0, network: "Bitcoin", crypto_id: "bitcoin" },
  ETH: { price: 0, network: "Ethereum", crypto_id: "ethereum" },
  USDT: { price: 0, network: "Ethereum", crypto_id: "tether" },
  BNB: { price: 0, network: "Binance Smart Chain", crypto_id: "binancecoin" },
  SOL: { price: 0, network: "Solana", crypto_id: "solana" },
};

interface CalculatedAmount {
  crypto_amount: number;
  currency: string;
  crypto_price: number;
  usd_amount: number;
  network: string;
  meets_minimum: boolean;
  minimum_required: number;
}

const Deposit: React.FC = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { transactions } = useSelector(
    (state: RootState) => state.transactions
  );
  const [loading, setLoading] = useState(false);
  const [fetchingPrices, setFetchingPrices] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [formData, setFormData] = useState({
    crypto_amount: "",
    currency: "BTC",
  });
  const [cryptoPrices, setCryptoPrices] = useState<CryptoPrice>(() => {
    // Try to load saved prices from localStorage on initial render
    const savedPrices = localStorage.getItem("obelisk_crypto_prices");
    if (savedPrices) {
      try {
        const parsed = JSON.parse(savedPrices);
        // Merge with initial currencies to ensure all required fields exist
        return { ...INITIAL_CURRENCIES, ...parsed };
      } catch (e) {
        return INITIAL_CURRENCIES;
      }
    }
    return INITIAL_CURRENCIES;
  });
  const [lastUpdate, setLastUpdate] = useState<string>(() => {
    // Try to load last update time from localStorage
    const savedUpdate = localStorage.getItem("obelisk_prices_last_update");
    return savedUpdate || "";
  });
  const [calculatedAmount, setCalculatedAmount] =
    useState<CalculatedAmount | null>(null);

  useEffect(() => {
    fetchCryptoPrices();
    dispatch(getTransactions({ type: "deposit" }) as any);

    // Refresh prices every 60 seconds
    const interval = setInterval(fetchCryptoPrices, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [dispatch]);

  const savePricesToStorage = (prices: CryptoPrice, timestamp: number) => {
    try {
      // Save prices to localStorage
      localStorage.setItem("obelisk_crypto_prices", JSON.stringify(prices));

      // Save last update timestamp
      localStorage.setItem(
        "obelisk_prices_last_update_timestamp",
        timestamp.toString()
      );

      // Save formatted last update time for display
      const updateTime = new Date(timestamp * 1000).toLocaleTimeString();
      setLastUpdate(updateTime);
      localStorage.setItem("obelisk_prices_last_update", updateTime);
    } catch (error) {
      console.error("Error saving prices to storage:", error);
    }
  };

  const fetchCryptoPrices = async () => {
    setFetchingPrices(true);
    try {
      const response = await transactionService.getCryptoPrices();

      if (response.success && response.prices) {
        // Merge new prices with existing ones (so we don't lose old values)
        const mergedPrices = { ...cryptoPrices };
        let hasNewData = false;

        Object.entries(response.prices).forEach(([currency, data]) => {
          // Type-safe check for price data
          const priceData = data as any;
          if (
            priceData &&
            typeof priceData === "object" &&
            typeof priceData.price === "number" &&
            priceData.price > 0
          ) {
            mergedPrices[currency] = priceData;
            hasNewData = true;
          }
        });

        // Only update and save if we got new valid data
        if (hasNewData) {
          setCryptoPrices(mergedPrices);
          savePricesToStorage(
            mergedPrices,
            response.timestamp || Math.floor(Date.now() / 1000)
          );

          // Show success toast only if we actually got new data
          if (Object.keys(response.prices).length > 0) {
            toast.success("Prices updated successfully");
          }
        } else {
          // No new valid data, keep existing prices
          // Use toast.success or toast.error instead of toast.info
        }
      } else {
        // API call succeeded but returned no data or error
        // Use toast.success or toast.error instead of toast.info
        toast.success("Using cached prices - API returned no data");

        // Load saved timestamp if available
        const savedTimestamp = localStorage.getItem(
          "obelisk_prices_last_update_timestamp"
        );
        if (savedTimestamp) {
          const timestamp = parseInt(savedTimestamp);
          const updateTime = new Date(timestamp * 1000).toLocaleTimeString();
          setLastUpdate(updateTime);
        }
      }
    } catch (error: any) {
      console.error("Price fetch error:", error);

      // On network error, use cached prices
      const savedTimestamp = localStorage.getItem(
        "obelisk_prices_last_update_timestamp"
      );
      if (savedTimestamp) {
        const timestamp = parseInt(savedTimestamp);
        const updateTime = new Date(timestamp * 1000).toLocaleTimeString();
        setLastUpdate(updateTime);
        // Use toast.error for error cases
        toast.error("Network error - Using cached prices");
      } else {
        toast.error("Failed to fetch prices - Please try again");
      }
    } finally {
      setFetchingPrices(false);
    }
  };

  const handleCalculate = async () => {
    if (!formData.crypto_amount || parseFloat(formData.crypto_amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    const currentPrice = cryptoPrices[formData.currency]?.price;
    if (!currentPrice || currentPrice === 0) {
      toast.error("Please wait for prices to load");
      return;
    }

    // Calculate locally first
    const cryptoAmount = parseFloat(formData.crypto_amount);
    const usdAmount = cryptoAmount * currentPrice;
    const minDepositUsd = 10;
    const meetsMinimum = usdAmount >= minDepositUsd;

    // Get network info from saved data or use default
    const network =
      cryptoPrices[formData.currency]?.network ||
      INITIAL_CURRENCIES[formData.currency as keyof typeof INITIAL_CURRENCIES]
        ?.network ||
      "Unknown Network";

    // Create local calculation
    const localCalculation: CalculatedAmount = {
      crypto_amount: cryptoAmount,
      currency: formData.currency,
      crypto_price: currentPrice,
      usd_amount: usdAmount,
      network: network,
      meets_minimum: meetsMinimum,
      minimum_required: minDepositUsd,
    };

    // Only validate with backend if minimum is met
    if (!meetsMinimum) {
      toast.error(
        `Minimum deposit is ${formatFiat(
          minDepositUsd
        )}. Your amount is ${formatFiat(usdAmount)}.`
      );
      setCalculatedAmount(localCalculation);
      return;
    }

    // If minimum is met, validate with backend (with current price)
    setCalculating(true);
    try {
      const response = await transactionService.calculateDepositAmount({
        crypto_amount: cryptoAmount,
        currency: formData.currency,
        current_price: currentPrice,
      });

      if (response.success) {
        setCalculatedAmount(response);
      } else {
        // If backend validation fails, still show local calculation
        setCalculatedAmount(localCalculation);
        if (response.error) {
          toast.error(response.error);
        }
      }
    } catch (error: any) {
      // If API call fails, use local calculation
      setCalculatedAmount(localCalculation);
      console.error("Backend calculation failed, using local:", error);
    } finally {
      setCalculating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.crypto_amount || parseFloat(formData.crypto_amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    if (!calculatedAmount) {
      toast.error("Please calculate the amount first");
      return;
    }

    if (!calculatedAmount.meets_minimum) {
      toast.error(
        `Minimum deposit is ${formatFiat(calculatedAmount.minimum_required)}`
      );
      return;
    }

    setLoading(true);
    try {
      await dispatch(
        deposit({
          crypto_amount: parseFloat(formData.crypto_amount),
          currency: formData.currency,
          current_price: calculatedAmount.crypto_price,
        }) as any
      );

      toast.success("Deposit submitted successfully!");

      // Reset form
      setFormData({
        crypto_amount: "",
        currency: "BTC",
      });
      setCalculatedAmount(null);

      // Refresh transactions
      dispatch(getTransactions({ type: "deposit" }) as any);
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to submit deposit");
    } finally {
      setLoading(false);
    }
  };

  const recentDeposits = transactions
    .filter((t) => t.type === "deposit")
    .slice(0, 3);

  const getCurrentPrice = (currency: string) => {
    return cryptoPrices[currency]?.price || 0;
  };

  const formatTransactionDetails = (transaction: any) => {
    if (
      transaction.admin_notes &&
      typeof transaction.admin_notes === "object"
    ) {
      const notes = transaction.admin_notes;
      return {
        cryptoAmount: notes.crypto_amount || 0,
        currency: notes.currency || "BTC",
        usdAmount: notes.usd_amount || transaction.amount,
        network: notes.network || "Unknown",
      };
    }
    return {
      cryptoAmount: 0,
      currency: "BTC",
      usdAmount: transaction.amount,
      network: "Unknown",
    };
  };

  // Format price display - shows dash for zero prices, otherwise shows saved price
  const formatPriceDisplay = (price: number, currency: string) => {
    if (price === 0) {
      return fetchingPrices ? "Loading..." : "—";
    }

    // Format based on currency value
    if (price > 1000) {
      return `$${price.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    } else if (price > 1) {
      return `$${price.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      })}`;
    } else {
      return `$${price.toLocaleString(undefined, {
        minimumFractionDigits: 6,
        maximumFractionDigits: 6,
      })}`;
    }
  };

  // Get last update time for display
  const getLastUpdateDisplay = () => {
    if (fetchingPrices) return "Updating...";
    if (lastUpdate) return `Last updated: ${lastUpdate}`;

    // Try to get from localStorage if state is empty
    const savedUpdate = localStorage.getItem("obelisk_prices_last_update");
    if (savedUpdate) {
      setLastUpdate(savedUpdate);
      return `Last updated: ${savedUpdate}`;
    }

    return "Never updated";
  };

  return (
    <div className={GlobalStyles.pageContainer}>
      <div className={GlobalStyles.contentContainer}>
        {/* Header */}
        <div className={GlobalStyles.sectionSpacing}>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className={GlobalStyles.heading1}>Make Deposit</h1>
              <p className={GlobalStyles.bodyText + " mt-2"}>
                Deposit cryptocurrency using from wallet
              </p>
            </div>
            <button
              onClick={fetchCryptoPrices}
              disabled={fetchingPrices}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} disabled:opacity-50`}
            >
              {fetchingPrices ? (
                <div className="flex items-center">
                  <div className={`${GlobalStyles.spinner} w-4 h-4 mr-2`}></div>
                  Updating...
                </div>
              ) : (
                <div className="flex items-center">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh Prices
                </div>
              )}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Deposit Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Price Ticker */}
            <div className={GlobalStyles.card}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={GlobalStyles.heading3}>Crypto Prices</h3>
                <div className="flex items-center gap-2 text-sm">
                  <TrendingUp
                    className={`h-4 w-4 ${
                      fetchingPrices ? "text-yellow-500" : "text-green-500"
                    }`}
                  />
                  <span
                    className={`${
                      fetchingPrices
                        ? "text-yellow-500"
                        : "text-gray-500 dark:text-gray-400"
                    }`}
                  >
                    {getLastUpdateDisplay()}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {Object.keys(INITIAL_CURRENCIES).map((currency) => {
                  const priceData = cryptoPrices[currency];
                  const price = priceData?.price || 0;
                  const network =
                    priceData?.network ||
                    INITIAL_CURRENCIES[
                      currency as keyof typeof INITIAL_CURRENCIES
                    ]?.network ||
                    "Unknown";

                  return (
                    <div
                      key={currency}
                      className={`p-3 rounded-lg text-center transition-all duration-200 ${
                        fetchingPrices
                          ? "bg-gray-100 dark:bg-gray-800/30 animate-pulse"
                          : price === 0
                          ? "bg-gray-100 dark:bg-gray-800/30"
                          : "bg-gray-50 dark:bg-gray-800/50"
                      }`}
                    >
                      <div className="font-semibold text-gray-900 dark:text-white mb-1">
                        {currency}
                      </div>
                      <div
                        className={`text-sm font-medium ${
                          fetchingPrices
                            ? "text-gray-400 dark:text-gray-500"
                            : price === 0
                            ? "text-gray-500 dark:text-gray-400"
                            : "text-green-600 dark:text-green-400"
                        }`}
                      >
                        {formatPriceDisplay(price, currency)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {network}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Deposit Form */}
            <div className={GlobalStyles.card}>
              <h3 className={`${GlobalStyles.heading3} mb-6`}>
                Deposit Details
              </h3>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Crypto Amount */}
                <div>
                  <label className={GlobalStyles.inputLabel}>
                    Cryptocurrency Amount
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.00000001"
                      value={formData.crypto_amount}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          crypto_amount: e.target.value,
                        })
                      }
                      className={`${GlobalStyles.input} pr-24`}
                      placeholder="0.00000000"
                      required
                      min="0.00000001"
                    />
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <select
                        value={formData.currency}
                        onChange={(e) =>
                          setFormData({ ...formData, currency: e.target.value })
                        }
                        className="bg-transparent border-none focus:outline-none text-gray-700 dark:text-gray-300"
                        disabled={fetchingPrices}
                      >
                        {Object.keys(INITIAL_CURRENCIES).map((currency) => (
                          <option key={currency} value={currency}>
                            {currency}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="flex justify-between mt-1">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Current price:{" "}
                      {formatPriceDisplay(
                        getCurrentPrice(formData.currency),
                        formData.currency
                      )}
                    </p>
                    {getCurrentPrice(formData.currency) > 0 &&
                      formData.crypto_amount && (
                        <p className="text-sm text-green-600 dark:text-green-400">
                          ≈{" "}
                          {formatFiat(
                            parseFloat(formData.crypto_amount) *
                              getCurrentPrice(formData.currency)
                          )}
                        </p>
                      )}
                  </div>
                </div>

                {/* Calculate Button */}
                <div>
                  <button
                    type="button"
                    onClick={handleCalculate}
                    disabled={
                      calculating ||
                      !formData.crypto_amount ||
                      fetchingPrices ||
                      getCurrentPrice(formData.currency) === 0
                    }
                    className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} w-full disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {calculating ? (
                      <div className="flex items-center justify-center">
                        <div
                          className={`${GlobalStyles.spinner} w-4 h-4 mr-2`}
                        ></div>
                        Calculating...
                      </div>
                    ) : fetchingPrices ? (
                      <div className="flex items-center justify-center">
                        <div
                          className={`${GlobalStyles.spinner} w-4 h-4 mr-2`}
                        ></div>
                        Loading Prices...
                      </div>
                    ) : getCurrentPrice(formData.currency) === 0 ? (
                      <div className="flex items-center justify-center">
                        <AlertCircle className="h-4 w-4 mr-2 text-yellow-500" />
                        No Price Available
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <Calculator className="h-4 w-4 mr-2" />
                        Calculate Amount
                      </div>
                    )}
                  </button>
                </div>

                {/* Calculated Amount Display */}
                {calculatedAmount && (
                  <div
                    className={`p-4 border rounded-lg ${
                      calculatedAmount.meets_minimum
                        ? "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800"
                        : "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4
                        className={`font-medium ${
                          calculatedAmount.meets_minimum
                            ? "text-green-800 dark:text-green-300"
                            : "text-yellow-800 dark:text-yellow-300"
                        }`}
                      >
                        Deposit Summary
                      </h4>
                      {calculatedAmount.meets_minimum ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-yellow-500" />
                      )}
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Cryptocurrency:
                        </span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {formatCrypto(
                            calculatedAmount.crypto_amount,
                            calculatedAmount.currency
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Exchange Rate:
                        </span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          1 {calculatedAmount.currency} = $
                          {calculatedAmount.crypto_price.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Network:
                        </span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {calculatedAmount.network}
                        </span>
                      </div>
                      <div className="pt-2 border-t border-current">
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            Total Deposit:
                          </span>
                          <span
                            className={`text-lg font-bold ${
                              calculatedAmount.meets_minimum
                                ? "text-green-600 dark:text-green-400"
                                : "text-yellow-600 dark:text-yellow-400"
                            }`}
                          >
                            {formatFiat(calculatedAmount.usd_amount)}
                          </span>
                        </div>
                      </div>
                      {!calculatedAmount.meets_minimum && (
                        <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
                          <p className="text-sm text-red-600 dark:text-red-400 text-center">
                            Minimum deposit:{" "}
                            {formatFiat(calculatedAmount.minimum_required)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <div>
                  <button
                    type="submit"
                    disabled={
                      loading ||
                      !calculatedAmount ||
                      fetchingPrices ||
                      !calculatedAmount?.meets_minimum
                    }
                    className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonLarge} w-full disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {loading ? (
                      <div className="flex items-center justify-center">
                        <div
                          className={`${GlobalStyles.spinner} w-5 h-5 mr-2`}
                        ></div>
                        Processing...
                      </div>
                    ) : fetchingPrices ? (
                      <div className="flex items-center justify-center">
                        <div
                          className={`${GlobalStyles.spinner} w-4 h-4 mr-2`}
                        ></div>
                        Loading...
                      </div>
                    ) : (
                      <>
                        <ArrowDownRight className="h-5 w-5 mr-2" />
                        Confirm Deposit
                      </>
                    )}
                  </button>
                  {!calculatedAmount && !fetchingPrices && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center mt-2">
                      Please calculate the amount first
                    </p>
                  )}
                  {calculatedAmount && !calculatedAmount.meets_minimum && (
                    <p className="text-sm text-red-500 dark:text-red-400 text-center mt-2">
                      Minimum deposit not met
                    </p>
                  )}
                </div>
              </form>
            </div>

            {/* Recent Deposits */}
            <div className={GlobalStyles.card}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={GlobalStyles.heading3}>Recent Deposits</h3>
                <button
                  onClick={() => (window.location.href = "/transactions")}
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonGhost} ${GlobalStyles.buttonSmall}`}
                >
                  <History className="h-4 w-4 mr-2" />
                  View All
                </button>
              </div>
              <div className="space-y-3">
                {recentDeposits.length > 0 ? (
                  recentDeposits.map((transaction) => {
                    const details = formatTransactionDetails(transaction);
                    return (
                      <div
                        key={transaction.id}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {formatCrypto(
                              details.cryptoAmount,
                              details.currency
                            )}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {formatFiat(details.usdAmount)} • {details.network}
                          </p>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                            {new Date(
                              transaction.created_at
                            ).toLocaleDateString()}{" "}
                            • {transaction.tx_hash?.substring(0, 12)}...
                          </p>
                        </div>
                        <span
                          className={`${GlobalStyles.badge} ${
                            transaction.status === "confirmed"
                              ? GlobalStyles.badgeSuccess
                              : transaction.status === "pending"
                              ? GlobalStyles.badgeWarning
                              : GlobalStyles.badgeError
                          }`}
                        >
                          {transaction.status}
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-4">
                    <p className="text-gray-500 dark:text-gray-400">
                      No deposits yet
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Info Panels */}
          <div className="space-y-6">
            {/* Important Info */}
            <div className={GlobalStyles.card}>
              <h3 className={`${GlobalStyles.heading3} mb-4`}>
                Important Information
              </h3>
              <div className="space-y-3">
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Minimum deposit: $10 USD equivalent
                  </p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Uses live prices
                  </p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    All deposits require confirmation
                  </p>
                </div>
              </div>
            </div>

            {/* How It Works */}
            <div className={GlobalStyles.card}>
              <h3 className={`${GlobalStyles.heading3} mb-4`}>How It Works</h3>
              <div className="space-y-3">
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                    <span className="text-xs font-medium text-primary">1</span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Enter cryptocurrency amount
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                    <span className="text-xs font-medium text-primary">2</span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    System uses current price
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                    <span className="text-xs font-medium text-primary">3</span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Validated with backend
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                    <span className="text-xs font-medium text-primary">4</span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Submit deposit for confirmation
                  </p>
                </div>
              </div>
            </div>

            {/* Current Balance */}
            <div className={GlobalStyles.card}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  Current Balance
                </h3>
                <WalletIcon className="h-5 w-5 text-primary" />
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-gray-900 dark:text-white">
                  {formatFiat(user?.balance || 0)}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Available for withdrawal
                </p>
              </div>
            </div>

            {/* Network Warning */}
            <div
              className={`${GlobalStyles.alert} ${GlobalStyles.alertWarning}`}
            >
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <p className="text-sm">
                Prices are cached. Will use last saved values if API fails.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Deposit;
